import { FC, useState } from 'react';
import classnames from 'classnames';
import { useSelector } from 'react-redux';
import { graphql, useFragment } from 'react-relay';
import { useAuctionPricing } from 'dibs-auctions/exports/hooks';

// components
import { Link } from 'dibs-elements/exports/Link';
import { FormattedMessage } from 'dibs-react-intl';
import { BidHistoryModalLazy } from 'dibs-auctions/exports/BidHistoryModalLazy';
import { PdpSharedAuctionTooltipLazy } from '../PdpSharedAuctionToolTip/PdpSharedAuctionTooltipLazy';

// types
import { PdpSharedAuctionBidsDetails_item$key as Item } from './__generated__/PdpSharedAuctionBidsDetails_item.graphql';

// styles
import styles from './PdpSharedAuctionBidsDetails.scss';

const itemFragment = graphql`
    fragment PdpSharedAuctionBidsDetails_item on Item {
        serviceId
        ...useAuctionPricing_item @arguments(page: pdp)
    }
`;

type Props = {
    item: Item;
    currency: string;
    className?: string;
};

type RelayVariables = {
    isClient: boolean;
};

type ReduxStoreType = {
    relayVariables: {
        variables: RelayVariables;
    };
};

export const PdpSharedAuctionBidsDetails: FC<Props> = ({ currency, className, item: itemRef }) => {
    const item = useFragment(itemFragment, itemRef);
    const { isClient } = useSelector<ReduxStoreType, RelayVariables>(
        relay => relay?.relayVariables?.variables || {}
    );
    const itemId = item?.serviceId;
    const [isBidModalOpen, setIsBidModalOpen] = useState<boolean>(false);
    const { totalBids, reserveMet, hasReserve } = useAuctionPricing({ item, currency });
    return (
        <>
            <div className={classnames(styles.container, className)}>
                <Link onClick={() => setIsBidModalOpen(true)} dataTn="pdp-bid-count">
                    <FormattedMessage
                        id="PdpSharedAuctionBidModal.numberOfBids"
                        defaultMessage="{totalBids, plural, one {# Bid} other {# Bids}}"
                        values={{ totalBids }}
                    />
                </Link>
                <span>{' | '}</span>
                <span data-tn="pdp-bid-status">
                    {!hasReserve && (
                        <FormattedMessage
                            id="PdpSharedAuctionBidModal.noReserve"
                            defaultMessage="No Reserve"
                        />
                    )}
                    {hasReserve && reserveMet && (
                        <FormattedMessage
                            id="PdpSharedAuctionBidModal.reserveMet"
                            defaultMessage="Reserve met"
                        />
                    )}
                    {hasReserve && !reserveMet && (
                        <FormattedMessage
                            id="PdpSharedAuctionBidModal.reserveNotMet"
                            defaultMessage="Reserve not met"
                        />
                    )}
                </span>
                <PdpSharedAuctionTooltipLazy type="reserve" />
            </div>
            {!!itemId && (
                <BidHistoryModalLazy
                    page="pdp"
                    itemId={itemId}
                    isClient={isClient}
                    currency={currency}
                    isOpen={isBidModalOpen}
                    onClose={() => setIsBidModalOpen(false)}
                />
            )}
        </>
    );
};
